import React, { useContext, useState } from 'react';
import { cloneDeep } from 'lodash';
import { assign } from 'xstate';
import { TextareaAutosize, Typography } from '@mui/material';
import { IntlShape } from 'react-intl';

import { SendContext } from '../../SendContext';
import { BaseComponent } from '../Component';
import { MachineContext } from '../../MachineContext';
import { translateWithFallback } from '../../util';
import { EvoachButton } from '../Button';
import { theme as evoachDefaultTheme } from '../../theme/theme';

import { useStyles } from './PostIt.styles';

export interface PostItInputComponent extends BaseComponent {
  type: 'postItInput';
  props: PostItInputProps;
}

export interface PostItInputProps {
  buttonText: string;
  saveResultTo: string;
  title: string;
  headline?: string;
  message?: string;
  minWidth?: number;
  maxWidth?: number;
  /**
   * has to be false by default as Creator editor hangs up with set to true
   * Is hard-coded set to true in renderAction, .i.e., for player only
   */
  autoFocus?: boolean;
}

export const PostItInput: React.FC<PostItInputProps> = ({
  buttonText,
  saveResultTo,
  title,
  headline,
  message,
  minWidth = 200,
  maxWidth = 250,
  autoFocus = false,
}) => {
  const { send } = useContext(SendContext);
  const [postItMessage, setPostItMessage] = useState<string>(message ?? '');
  const classes = useStyles();

  const sendMessage = () => {
    send({
      type: 'writeToContext',
      payload: {
        [saveResultTo]: postItMessage,
      },
    });
    send({
      type: 'next',
    });
  };

  return (
    <div
      className={classes.postItInput}
      style={{ minWidth: minWidth, maxWidth: maxWidth }}
    >
      <div className={classes.postIt}>
        <Typography className={classes.headline} variant="h5">
          {headline}
        </Typography>
        <Typography className={classes.title} variant="h6">
          {title}
        </Typography>

        <TextareaAutosize
          autoFocus={autoFocus}
          spellCheck={false}
          style={{
            background: '#fcf4e7',
            resize: 'none',
            fontFamily: evoachDefaultTheme.typography.body2.fontFamily,
            fontSize: evoachDefaultTheme.typography.body2.fontSize,
            outline: 'none',
          }}
          value={postItMessage}
          onChange={(event) => {
            const value = event.target.value;
            setPostItMessage(value);
          }}
          onKeyDown={(event) => {
            if (
              (event.key === 'Enter' || event.code === 'NumpadEnter') &&
              (event.metaKey || event.ctrlKey)
            ) {
              sendMessage();
            }
          }}
        />
      </div>
      <EvoachButton onClick={sendMessage}>
        <Typography variant="button">{buttonText}</Typography>
      </EvoachButton>
    </div>
  );
};

export const renderPostItInputAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;
    const message = getValueFrom
      ? context.userData[getValueFrom]
      : payload.message;

    newContext.widgetData.push({
      type: 'postItInput',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        autoFocus: true,
        buttonText: translateWithFallback(intl, payload.buttonText),
        saveResultTo: payload.saveResultTo,
        headline: translateWithFallback(intl, payload.headline),
        title: translateWithFallback(intl, payload.title),
        message: message ?? '',
      },
    });
    return newContext;
  });
};
